import React, { useEffect } from "react";
import {
  Box,
  ColorSchemeProvider,
  MantineProvider,
  Text,
  Title,
} from "@mantine/core";
import { keyframes } from "@emotion/react";

const float = keyframes({
  "0%, 100%": { transform: "translate3d(0, 0, 0) scale(1)" },
  "50%": { transform: "translate3d(12px, -22px, 0) scale(1.08)" },
});

const driftGrid = keyframes({
  from: { backgroundPosition: "0 0, 0 0" },
  to: { backgroundPosition: "48px 48px, 48px 48px" },
});

const sheen = keyframes({
  "0%": { transform: "translate3d(-55%, -8%, 0)" },
  "100%": { transform: "translate3d(55%, 8%, 0)" },
});

const motion = {
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
};

const brandName = process.env.REACT_APP_NAME || "MBID";
const brandSubtitle =
  process.env.REACT_APP_SUBTITLE || "Insurance Broker System";
const productTitle = process.env.REACT_APP_WEB_TITLE || "MBID System";
const version = process.env.REACT_APP_VERSION || "";

const brand = {
  deep: "#0a656c",
  mid: "#0e8a84",
  bright: "#14a39a",
  glow: "#7dceaf",
  mist: "#d7f5ec",
  ink: "#0c5c56",
};

const wordmarkSx = {
  color: "#ffffff",
  fontFamily: '"Montserrat", "Arial Black", sans-serif',
  fontStyle: "italic",
  fontWeight: 800,
  letterSpacing: "0.14em",
  lineHeight: 1,
  textTransform: "uppercase",
};

const BrandWordmark = ({ size = 28 }) => (
  <Text component="span" sx={{ ...wordmarkSx, fontSize: size }}>
    {brandName}
  </Text>
);

const AuthSplitLayout = ({
  title = "Welcome back",
  subtitle = "Sign in to your account to continue",
  children,
}) => {
  useEffect(() => {
    const id = "auth-wordmark-font";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@1,800&display=swap";
    document.head.appendChild(link);
  }, []);

  return (
    <ColorSchemeProvider colorScheme="light" toggleColorScheme={() => {}}>
      <MantineProvider
        inherit
        theme={{ colorScheme: "light", primaryColor: "teal", primaryShade: 7 }}
        withGlobalStyles
        withNormalizeCSS
      >
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            background: "#ffffff",
            color: "#0f172a",
          }}
        >
          <Box
              sx={{
                display: "none",
                width: "50%",
                position: "relative",
                overflow: "hidden",
                background: `linear-gradient(to bottom right, ${brand.mid}, #14988f, ${brand.deep})`,
                "@media (min-width: 992px)": {
                  display: "flex",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px)",
                  backgroundSize: "48px 48px",
                  opacity: 0.35,
                  animation: `${driftGrid} 22s linear infinite`,
                  ...motion,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: "-20%",
                  bottom: "-20%",
                  left: "-30%",
                  width: "70%",
                  background:
                    "linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.16) 48%, transparent 66%)",
                  animation: `${sheen} 16s ease-in-out infinite alternate`,
                  pointerEvents: "none",
                  ...motion,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 80,
                  left: 80,
                  width: 288,
                  height: 288,
                  borderRadius: "50%",
                  background: brand.bright,
                  opacity: 0.22,
                  filter: "blur(64px)",
                  animation: `${float} 8s ease-in-out infinite`,
                  ...motion,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 80,
                  right: 80,
                  width: 384,
                  height: 384,
                  borderRadius: "50%",
                  background: brand.glow,
                  opacity: 0.16,
                  filter: "blur(72px)",
                  animation: `${float} 10s ease-in-out 2s infinite`,
                  ...motion,
                }}
              />

              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  padding: 48,
                  width: "100%",
                }}
              >
                <BrandWordmark />

                <Box sx={{ maxWidth: 460 }}>
                  <Title
                    order={1}
                    sx={{
                      color: "#ffffff",
                      fontSize: 40,
                      lineHeight: 1.15,
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    {brandName}
                  </Title>
                  <Text
                    sx={{
                      color: brand.mist,
                      fontSize: 32,
                      lineHeight: 1.2,
                      fontWeight: 700,
                    }}
                  >
                    {brandSubtitle}
                  </Text>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    color: brand.mist,
                    fontSize: 14,
                  }}
                >
                  <span>{productTitle}</span>
                  <span>{version}</span>
                </Box>
              </Box>
            </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
              background: "#ffffff",
            }}
          >
            <Box
                sx={{
                  padding: 24,
                  borderBottom: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  "@media (min-width: 992px)": {
                    display: "none",
                  },
                }}
              >
                <Text
                  component="span"
                  sx={{ ...wordmarkSx, fontSize: 22, color: brand.ink }}
                >
                  {brandName}
                </Text>
              </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "48px 24px",
              }}
            >
              <Box sx={{ width: "100%", maxWidth: 420 }}>
                <Box sx={{ marginBottom: 32 }}>
                  <Title
                    order={2}
                    sx={{
                      color: "#0f172a",
                      fontSize: 24,
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    {title}
                  </Title>
                  <Text sx={{ color: "#64748b", fontSize: 15 }}>{subtitle}</Text>
                </Box>

                {children}

                <Box
                  sx={{
                    marginTop: 32,
                    paddingTop: 24,
                    borderTop: "1px solid #e2e8f0",
                    textAlign: "center",
                  }}
                >
                  <Text sx={{ color: "#64748b", fontSize: 14 }}>
                    Need help? Contact your system administrator
                  </Text>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "none",
                padding: 24,
                textAlign: "center",
                color: "#94a3b8",
                fontSize: 14,
                borderTop: "1px solid #f1f5f9",
                "@media (min-width: 992px)": {
                  display: "block",
                },
              }}
            >
              {productTitle}
            </Box>
          </Box>
        </Box>
      </MantineProvider>
    </ColorSchemeProvider>
  );
};

export const authFieldStyles = {
  label: {
    fontWeight: 500,
    color: "#334155",
    marginBottom: 6,
  },
  input: {
    height: 44,
    borderRadius: 10,
    borderColor: "#e2e8f0",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    "&:focus": {
      borderColor: brand.bright,
    },
  },
  innerInput: {
    height: 44,
    color: "#0f172a",
  },
  icon: {
    color: "#94a3b8",
  },
  visibilityToggle: {
    color: "#64748b",
  },
};

export default AuthSplitLayout;
